import { xor } from 'lodash';
import { getVariantsSortedByPrice } from '../../products';
import { ApiResult } from '../../api';
import {
  ACTIONS,
  ACTION_TYPES,
  STATE,
  pathToAction,
  ALL_RECIPES,
} from './constants';

export const getLowestCostVariant = starterBoxVariants => {
  const variants = getVariantsSortedByPrice(starterBoxVariants);
  return variants[0];
};

export const getRecipeCombination = (recipeCombinations, recipes) => {
  const variant = recipeCombinations.find(
    variant =>
      !xor(variant.recipes.fresh, recipes.fresh).length &&
      !xor(variant.recipes.baked, recipes.baked).length,
  );
  return variant;
};

export const saveCart = async (ctx, payload) => {
  const {
    reporter,
    combinationId,
    setShowModal,
    setLocalSelection,
    cartProduct,
    petId,
    cartApi,
    defaultCadence,
  } = payload.props;
  // If the same meal plan is already in cart, no need to update
  const lineItemForPet = cartProduct;
  if (combinationId === lineItemForPet?.combination_id) {
    return ApiResult.success();
  }

  const selectedCadence = payload.props?.selectedCadence;

  let result;
  result = await cartApi.upsertMealPlan(petId, {
    combination_id: combinationId,
    ...(selectedCadence ? { subscription_cadence: selectedCadence } : {}),
  });

  const petsInCart = result?.payload?.pets;
  const starterBoxCadence = petsInCart?.[0]?.starter_box?.cadence;

  if (!!starterBoxCadence && !!defaultCadence) {
    cartApi.updateCartAttributes({
      starter_box_cadence: petsInCart?.length === 1 ? starterBoxCadence : null,
      subscription_default_cadence:
        petsInCart?.length === 1 ? defaultCadence : null,
    });
  }

  ApiResult.match(result, {
    success: () => setLocalSelection(null, { skipRender: true }),
    error: {
      client: error => {
        reporter.error(error.original);
        setShowModal(true);
      },
      server: () => setShowModal(true),
    },
  });

  return result;
};

export const onCartSubmission = service => {
  if (service.machine.current !== STATE.submitCart) {
    return Promise.resolve(ApiResult.success());
  }
  return new Promise(resolve => {
    const checkSuccess = setInterval(() => {
      if (service.machine.current !== STATE.submitCart) {
        clearInterval(checkSuccess);
        resolve(service.context.submitCartResult);
      }
    }, 500);
  });
};

/**
 * Accepts a pathname as a string and returns the corresponding stateKey
 * @param {string} pathname
 * @param {string} [rootPath='']
 * @returns {string}
 */
export const getKeyFromPath = (pathname, rootPath) => {
  return pathname.replace(rootPath, '').replace(/\/$/, ''); // remove rootpath, then remove any trailing '/'
};

export const getActionFromKey = (key, service) => {
  const action = pathToAction[key];

  const machine = service.machine.original ?? service.machine;
  const transitions = machine.states[service.context.stateKey].transitions;

  if (action && Array.from(transitions.keys()).includes(action({}).type))
    return action;

  for (let [transitionKey, values] of transitions) {
    if (transitionKey === ACTION_TYPES.goBack) {
      for (let value of values) {
        if (value.guards(service.context, {})) return ACTIONS.goBack;
      }
    }
  }

  return action;
};

export const goBackReducer = (ctx, { payload }) => {
  const newCtx = {
    ...ctx,
    initialFlow: false,
    pets: payload.pets ?? ctx.pets,
    pet: payload.pet ?? ctx.pet,
    pet_id: payload.pet_id ?? ctx.pet_id,
    cart: payload.cart ?? ctx.cart,
    customer: payload.customer ?? ctx.customer,
  };

  // If customer goes back a step, and we'd set to an empty pet id,
  // and the customer actually has a pet available,
  // grab the pet id from that existing pet and set it
  // as our pet_id.
  if (newCtx.pet_id === '' && newCtx.pets?.[0]?.id) {
    newCtx.pet_id = newCtx.pets?.[0]?.id;
  }
  return newCtx;
};

export const getUnavailableRecipes = availableRecipes =>
  ALL_RECIPES.filter(recipe => !availableRecipes.includes(recipe));

export const getSelectedAllergies = (selectedAllergies, allergies) => {
  return allergies?.filter(allergy =>
    selectedAllergies?.some(sel => sel.name === allergy.name),
  );
};

export const normalize = allergies =>
  allergies?.map(allergy => ({
    label: allergy.name,
    value: allergy.id,
  }));
